import { makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { ErrorOutline } from '@material-ui/icons';
import React from 'react';
import { Check, Edit2, Trash } from 'react-feather';
import { TFunction, useTranslation } from 'react-i18next';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { Plan } from '../../../../types/plan';
import { Button, IconButton } from '../../../global';
import { Property } from '../../../global/Property';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.Thickness';

/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${!focused ? '-small' : ''}`),
    subtitle: t(`${mod}.header-subtitle${!focused ? '-small' : ''}`, { count: 1, document: 'API653' }),
});

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flex: 1,
        margin: '0 -10px',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '0 10px',
    },
    button: {
        alignSelf: 'center',
        marginTop: 20,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        padding: '15px 20px',
        borderRadius: 4,
        background: theme.colors.blue[3],
        color: theme.colors.darkBlue[1],
        ...theme.typography.content.label.default,
        '& > svg': {
            width: 20,
            height: 20,
            marginRight: 15,
            color: theme.colors.blue[2],
        },
    },
    description: {
        ...theme.typography.content.caption.paragraph,
        marginTop: 20,
    },
});

/**
 * Minimum required thickness section of the inspection creation process.
 *
 * @param props.state the current section state
 * @param props.next function to advance to the next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useComponentClasses();
    const { t } = useTranslation(['inspection-creation']);
    const dispatch = useTypedDispatch();
    const { plan } = useTypedSelector(state => ({ plan: state.plan.changed }));

    const addThicknessRequirement = () => {
        const thicknessItem = { component: '', thickness: '0', unit: 'in' };
        const thickness = [...(plan?.thickness_requirement?.thickness ?? []), thicknessItem];
        const thickness_requirement = { id: -1, description: '', ...plan?.thickness_requirement, thickness };
        dispatch(actions.plan.edit({ thickness_requirement }));
    };

    if (!plan) return <span>{'#plan not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.column}>
                <ThicknessTable state={props.state} plan={plan} />
                {props.state.editable && (
                    <Button className={classes.button} variant='contained' onClick={addThicknessRequirement}>
                        {t(`${mod}.add-component`)}
                    </Button>
                )}
            </div>
            <div className={classes.column}>
                <div className={classes.header}>
                    <ErrorOutline />
                    {t(`${mod}.reference-title`)}
                </div>

                <div className={classes.description}>
                    <span>{plan.thickness_requirement?.description}</span>
                </div>
            </div>
        </div>
    );
};

const useThicknessTableClasses = makeStyles({
    root: {},
    header: {
        borderBottom: 'none',
        flex: '1',
    },
});

const ThicknessTable = (props: { state: SectionState; plan: Plan }) => {
    const classes = useThicknessTableClasses();
    const { t } = useTranslation(['inspection-creation']);

    return (
        <TableContainer>
            <Table className={classes.root}>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.header}>{t(`${mod}.table-header-component`)}</TableCell>
                        <TableCell className={classes.header}>{t(`${mod}.table-header-required-thickness`)}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(props.plan.thickness_requirement?.thickness ?? []).map((_, i) => (
                        <ThicknessRow key={i} plan={props.plan} index={i} editable={props.state.editable} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const useThicknessRowClasses = makeStyles({
    root: {},
    cell: {
        width: '50%',
        padding: 10,
    },
    cellContent: {
        display: 'flex',
        alignItems: 'center',
    },
    property: {
        marginBottom: '0 !important',
        '& > span:first-child': {
            display: 'none',
        },
        '& > span:last-child': {
            ...theme.typography.content.caption.paragraph,
            marginBottom: 0,
        },
        '&.MuiFormControl-root': {
            minWidth: 50,
        },
        '& .MuiOutlinedInput-input': {
            padding: '10px 8px',
            ...theme.typography.content.caption.paragraph,
        },
    },
    icon: {
        padding: 8,
        margin: '0 5px',
        color: theme.colors.neutral[1],
    },
});

const ThicknessRow = (props: { plan: Plan; index: number; editable: boolean }) => {
    const classes = useThicknessRowClasses();
    const dispatch = useTypedDispatch();
    const thickness = { ...props.plan.thickness_requirement?.thickness[props.index]! };
    const [editMode, setEditMode] = React.useState(thickness.component.length === 0);
    const editing = props.editable && editMode;
    const units = ['in', 'cm', 'mm'];

    const updateThickness = (changes: Partial<typeof thickness>): void => {
        const updatedThickness = { ...thickness, ...changes };
        const thicknesses = [...(props.plan?.thickness_requirement?.thickness ?? [])];
        thicknesses.splice(props.index, 1, updatedThickness);
        const thickness_requirement = { ...props.plan.thickness_requirement!, thickness: thicknesses };
        dispatch(actions.plan.edit({ thickness_requirement }));
    };

    const deleteThickness = () => {
        const thicknesses = [...(props.plan?.thickness_requirement?.thickness ?? [])];
        thicknesses.splice(props.index, 1);
        const thickness_requirement = { ...props.plan.thickness_requirement!, thickness: thicknesses };
        dispatch(actions.plan.edit({ thickness_requirement }));
    };

    return (
        <TableRow className={classes.root}>
            <TableCell className={classes.cell} width='40%'>
                <Property
                    key={Math.random().toString()}
                    className={classes.property}
                    value={thickness.component}
                    placeholder='Component'
                    editable={editing}
                    onChange={(name, value) => updateThickness({ component: value })}
                />
            </TableCell>
            <TableCell className={classes.cell}>
                <div className={classes.cellContent}>
                    <Property
                        key={Math.random().toString()}
                        className={classes.property}
                        value={thickness.thickness}
                        placeholder='Size'
                        editable={editing}
                        onChange={(name, value) => updateThickness({ thickness: value })}
                    />
                    <div style={{ minWidth: 10 }} />
                    <Property
                        key={Math.random().toString()}
                        className={classes.property}
                        value={thickness.unit}
                        options={[...(units.includes(thickness.unit) ? [] : [thickness.unit]), ...units]}
                        editable={editing}
                        onChange={(name, value) => updateThickness({ unit: value })}
                    />
                    <div style={{ minWidth: 10 }} />
                    <div style={{ flex: 1 }} />
                    <IconButton
                        key={editing.toString()}
                        className={classes.icon}
                        size='small'
                        onClick={() => setEditMode(!editMode)}
                    >
                        {editing ? <Check /> : <Edit2 />}
                    </IconButton>
                    {editing && (
                        <IconButton className={classes.icon} size='small' onClick={deleteThickness}>
                            <Trash />
                        </IconButton>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
};
